import {
  Apple,
  ArrowRight,
  Check,
  ChevronRight,
  Code2,
  Inbox,
  LockKeyhole,
  Mail,
  Monitor,
  MoreHorizontal,
  Paperclip,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Terminal,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

const platforms = [
  { name: 'Windows', icon: Monitor, iconClass: 'bg-[#e8f1ff] text-[#3571b8]' },
  { name: 'Mac', icon: Apple, iconClass: 'bg-[#f1eded] text-[#403836]' },
  { name: 'Linux', icon: Terminal, iconClass: 'bg-[#fff2d9] text-[#9a681b]' },
  { name: 'Android', icon: Smartphone, iconClass: 'bg-[#e8f6ec] text-[#388451]' },
  { name: 'iOS', icon: Apple, iconClass: 'bg-[#eef0f4] text-[#4b5568]' },
]

function PlatformTicker() {
  return (
    <div
      className="platform-viewport mx-auto mt-6 max-w-2xl overflow-hidden"
      aria-label="NewEmail is available on Windows, Mac, Linux, Android, and iOS"
    >
      <p className="sr-only">Available on Windows, Mac, Linux, Android, and iOS.</p>
      <div className="platform-roll flex w-max items-center">
        {[0, 1].map((copy) => (
          <div
            key={copy}
            className="flex shrink-0 items-center gap-2.5 pr-2.5"
            data-platform-copy={copy === 1 ? 'duplicate' : 'primary'}
            aria-hidden="true"
          >
            {platforms.map(({ name, icon: Icon, iconClass }) => (
              <span
                key={name}
                className="inline-flex h-9 items-center gap-2 rounded-full border border-[#e7d4c8] bg-white/80 px-3.5 text-sm font-extrabold text-[#4f3b31] shadow-[0_5px_16px_-8px_rgba(91,48,29,0.45)] backdrop-blur-sm"
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full ${iconClass}`}
                >
                  <Icon className="h-3 w-3" strokeWidth={2.4} />
                </span>
                {name}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const messages = [
  {
    initials: 'MR',
    name: 'Maya Rodriguez',
    subject: 'Launch week: final checklist',
    preview: 'I dropped the final copy and QA notes into the shared folder.',
    time: '9:42',
    color: 'bg-[#e3e9ff] text-[#475895]',
    unread: true,
  },
  {
    initials: 'AL',
    name: 'Avery Lewis',
    subject: 'Coffee next week?',
    preview: 'I found a little place around the corner…',
    time: '9:18',
    color: 'bg-[#d9ecff] text-[#315b7d]',
    unread: true,
  },
  {
    initials: 'NP',
    name: 'Northstar Projects',
    subject: 'Invoice 2481 has been paid',
    preview: 'Payment confirmation and receipt are attached.',
    time: '8:55',
    color: 'bg-[#e4e0ff] text-[#554a91]',
    unread: false,
  },
  {
    initials: 'JL',
    name: 'Jamie Lin',
    subject: 'Photos from the weekend',
    preview: 'The light was unreal. Here are my favorites.',
    time: 'Yesterday',
    color: 'bg-[#ffe5d2] text-[#8d4a25]',
    unread: false,
  },
  {
    initials: 'CT',
    name: 'Cedar & Thread',
    subject: 'Your order has shipped',
    preview: 'Tracking information is inside this message.',
    time: 'Tue',
    color: 'bg-[#ffd7d2] text-[#8f322b]',
    unread: false,
  },
  {
    initials: 'DW',
    name: 'Design Weekly',
    subject: 'The small details that make software feel calm',
    preview: 'This week: thoughtful defaults, better empty states…',
    time: 'Mon',
    color: 'bg-[#dcefe1] text-[#35674a]',
    unread: false,
  },
]

function DesktopInboxPreview() {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[#d8c7bb] bg-[#fbf3ec] shadow-[0_42px_100px_-28px_rgba(89,42,23,0.55)]">
      <div className="flex h-11 items-center border-b border-black/10 bg-[#eadfd8] px-4">
        <div className="flex gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#f47b69]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#e8bd4b]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#68b87c]" />
        </div>
        <div className="mx-auto hidden h-6 min-w-52 items-center justify-center gap-1.5 rounded-md border border-black/5 bg-white/55 px-5 text-[9px] font-semibold text-[#6b574b] sm:flex">
          <LockKeyhole className="h-2.5 w-2.5" />
          app.new-email.net/inbox
        </div>
        <div className="ml-auto flex items-center gap-3 text-[#715e52] sm:ml-0">
          <Settings className="h-3.5 w-3.5" />
        </div>
      </div>

      <div className="flex min-h-[510px] text-left">
        <aside className="hidden w-[196px] shrink-0 flex-col bg-[#35221d] p-4 text-white lg:flex">
          <div className="flex items-center gap-2 text-sm font-extrabold">
            <img
              src="/newemail-logo.svg"
              alt=""
              className="h-7 w-7 rounded-lg"
              width={28}
              height={28}
            />
            NewEmail
          </div>
          <div className="mt-5 flex h-9 items-center justify-center gap-2 rounded-lg bg-primary text-xs font-bold shadow-[0_8px_20px_rgba(235,90,28,0.24)]">
            <Send className="h-3.5 w-3.5" />
            New message
          </div>
          <div className="mt-5 space-y-1 text-[11px] text-white/68">
            {['Inbox', 'Starred', 'Sent', 'Scheduled', 'Archive'].map((label, index) => (
              <div
                key={label}
                className={`flex items-center justify-between rounded-md px-3 py-2 ${
                  index === 0 ? 'bg-white/11 text-white' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  {index === 0 ? <Inbox className="h-3.5 w-3.5" /> : null}
                  {label}
                </span>
                {index === 0 && (
                  <span className="rounded-full bg-primary px-1.5 py-0.5 text-[9px] text-white">4</span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-6">
            <p className="px-3 text-[8px] font-bold uppercase tracking-[0.18em] text-white/35">
              Accounts
            </p>
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 rounded-md bg-white/8 px-3 py-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[8px] font-black text-[#db4437]">
                  G
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-semibold leading-tight tracking-[-0.01em] text-white/90">
                    avery@gmail.com
                  </span>
                  <span className="mt-0.5 block truncate text-[8px] font-medium leading-none text-white/38">
                    Google
                  </span>
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-[#7bd398]" />
              </div>
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#d9ecff] text-[8px] font-black text-[#315b7d]">
                  A
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-semibold leading-tight tracking-[-0.01em] text-white/78">
                    avery@icloud.com
                  </span>
                  <span className="mt-0.5 block truncate text-[8px] font-medium leading-none text-white/35">
                    Apple iCloud
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="mt-auto rounded-lg border border-white/8 bg-white/5 p-3 text-[9px] leading-relaxed text-white/52">
            <ShieldCheck className="mb-2 h-4 w-4 text-[#ff9b65]" />
            Passwords stay in your system keychain.
          </div>
        </aside>

        <div className="w-full border-r border-[#e7d8ce] bg-[#fffaf5] md:w-[44%] lg:w-[37%]">
          <div className="border-b border-[#e7d8ce] px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  Inbox
                </p>
                <p className="mt-1 text-sm font-extrabold tracking-tight">Good morning, Avery</p>
              </div>
              <span className="rounded-full bg-[#f3e6dc] px-2 py-1 text-[9px] font-bold text-[#7d6658]">
                4 new
              </span>
            </div>
            <div className="mt-3 flex h-8 items-center gap-2 rounded-lg border border-[#eadbd1] bg-white px-2.5 text-[10px] text-muted-foreground">
              <Search className="h-3.5 w-3.5" />
              Search all mail
              <span className="ml-auto rounded border border-[#eadbd1] px-1 text-[8px]">⌘ K</span>
            </div>
          </div>
          {messages.map((message, index) => (
            <div
              key={message.name}
              className={`flex gap-2.5 border-b border-[#f0e4db] px-3.5 py-3 ${
                index === 0 ? 'border-l-2 border-l-primary bg-[#fff2e8]' : ''
              }`}
            >
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${message.color}`}
              >
                {message.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="flex min-w-0 items-center gap-1.5 truncate text-[10px] font-bold">
                    {message.unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                    {message.name}
                  </p>
                  <span className="shrink-0 text-[8px] text-muted-foreground">{message.time}</span>
                </div>
                <p className="mt-0.5 truncate text-[10px] font-semibold">{message.subject}</p>
                <p className="mt-0.5 truncate text-[9px] text-muted-foreground">{message.preview}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden min-w-0 flex-1 bg-white md:block">
          <div className="flex items-center justify-between border-b border-[#eee2d9] px-5 py-3 text-muted-foreground">
            <div className="flex gap-4">
              <Inbox className="h-3.5 w-3.5" />
              <Star className="h-3.5 w-3.5" />
              <Paperclip className="h-3.5 w-3.5" />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[9px]">1 of 42</span>
              <Settings className="h-3.5 w-3.5" />
            </div>
          </div>
          <article className="px-5 py-5 lg:px-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary">
                  Project update
                </p>
                <h3 className="mt-1.5 max-w-sm text-[17px] font-extrabold leading-tight tracking-tight">
                  Launch week: final checklist
                </h3>
              </div>
              <Star className="mt-1 h-3.5 w-3.5 text-[#c5b3a8]" />
            </div>
            <div className="mt-4 flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e3e9ff] text-[9px] font-bold text-[#475895]">
                MR
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-bold">Maya Rodriguez</p>
                <p className="text-[9px] text-muted-foreground">maya@northstar.studio · 9:42 AM</p>
              </div>
              <MoreHorizontal className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="my-4 h-px bg-[#eee2d9]" />
            <div className="space-y-2.5 text-[10px] leading-relaxed text-[#5b4b42]">
              <p>Hey Avery,</p>
              <p>
                Everything is looking good for launch. I pulled the final copy, QA notes, and
                release links into one place so we can move quickly tomorrow.
              </p>
              <div className="rounded-lg border border-[#eee2d9] bg-[#fffaf6] p-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#ffe3d1] text-primary">
                    <Paperclip className="h-3 w-3" />
                  </span>
                  <div>
                    <p className="text-[9px] font-bold">Launch-checklist.pdf</p>
                    <p className="text-[8px] text-muted-foreground">PDF · 1.8 MB</p>
                  </div>
                  <ChevronRight className="ml-auto h-3 w-3 text-muted-foreground" />
                </div>
              </div>
              <p>Let&apos;s do one last pass at 4:30 and press go.</p>
              <p className="pt-1">Talk soon,<br />Maya</p>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#e7d8ce] px-3 py-2 text-[9px] text-muted-foreground">
              <span className="flex-1">Reply to Maya…</span>
              <Send className="h-3 w-3 text-primary" />
            </div>
          </article>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[#e5d6cc] bg-[#fff3ea] px-4 py-2 text-[9px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#54ad70]" />
          All accounts synced
        </span>
        <span>Updated just now · 1,284 messages offline</span>
      </div>
    </div>
  )
}

function AccountSetupPreview() {
  return (
    <div
      className="w-full max-w-[370px] rounded-[22px] border border-[#d9c5b8] bg-[#fffaf6] p-5 text-left shadow-[0_28px_70px_-20px_rgba(52,27,17,0.55)]"
      aria-label="Add your account automatic configuration preview"
    >
      <div className="flex items-start justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white shadow-[0_8px_20px_rgba(235,90,28,0.28)]">
          <Mail className="h-5 w-5" />
        </span>
        <span className="rounded-full border border-[#cfe7d7] bg-[#eff9f1] px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.11em] text-[#36754b]">
          Auto-configured
        </span>
      </div>
      <h3 className="mt-4 text-xl font-extrabold tracking-tight">Add your account</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Enter your email. NewEmail finds the right server settings for you.
      </p>

      <p className="mt-4 text-[9px] font-bold uppercase tracking-[0.13em] text-[#7b6659]">
        Email address
      </p>
      <div className="mt-1.5 flex h-10 items-center gap-2 rounded-lg border-2 border-[#e6d4c8] bg-white px-3 text-xs font-semibold">
        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
        avery@gmail.com
        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-[#e8f6ec]">
          <Check className="h-3 w-3 text-[#3b8a53]" />
        </span>
      </div>

      <div className="mt-3 rounded-xl border border-[#cfe7d7] bg-[#f2fbf4] p-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#dff3e4] text-[#36754b]">
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <div>
            <p className="text-[11px] font-extrabold text-[#285d3c]">Account settings found</p>
            <p className="text-[9px] text-[#4c7a5b]">Gmail · Secure connection</p>
          </div>
        </div>
        <div className="mt-3 space-y-2 rounded-lg bg-white/75 p-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] text-muted-foreground">Incoming</span>
            <span className="truncate font-mono text-[9px] font-semibold">imap.gmail.com</span>
            <span className="shrink-0 rounded bg-[#e7f3ea] px-1.5 py-0.5 text-[8px] font-bold text-[#36754b]">
              SSL 993
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[9px] text-muted-foreground">Outgoing</span>
            <span className="truncate font-mono text-[9px] font-semibold">smtp.gmail.com</span>
            <span className="shrink-0 rounded bg-[#e7f3ea] px-1.5 py-0.5 text-[8px] font-bold text-[#36754b]">
              SSL 465
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-[#eadbd1] bg-white px-3 py-2.5">
        <div>
          <p className="text-[10px] font-bold">Use these settings automatically</p>
          <p className="mt-0.5 text-[8px] text-muted-foreground">You can edit them anytime.</p>
        </div>
        <span className="flex h-5 w-9 items-center rounded-full bg-primary p-0.5">
          <span className="ml-auto h-4 w-4 rounded-full bg-white shadow-sm" />
        </span>
      </div>

      <div className="mt-3 flex h-10 items-center justify-center gap-2 rounded-lg bg-primary text-xs font-extrabold text-white shadow-[0_8px_20px_rgba(235,90,28,0.22)]">
        Connect account
        <ArrowRight className="h-3.5 w-3.5" />
      </div>
      <p className="mt-2.5 flex items-center justify-center gap-1.5 text-[8px] text-muted-foreground">
        <LockKeyhole className="h-2.5 w-2.5" />
        Credentials are protected by your system keychain.
      </p>
    </div>
  )
}

function MobileInboxPreview() {
  return (
    <div className="w-[184px] overflow-hidden rounded-[30px] border-[7px] border-[#2e1d18] bg-[#fffaf5] shadow-[0_30px_60px_-18px_rgba(48,24,15,0.65)]">
      <div className="relative h-7 bg-[#35221d]">
        <span className="absolute left-1/2 top-1.5 h-3 w-14 -translate-x-1/2 rounded-full bg-[#21140f]" />
      </div>
      <div className="px-3 pb-2 pt-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[8px] font-semibold text-muted-foreground">Inbox</p>
            <p className="text-[13px] font-extrabold tracking-tight">Good morning</p>
          </div>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#ffe1cd] text-[8px] font-black text-primary">
            AL
          </span>
        </div>
        <div className="mt-2 flex h-7 items-center gap-1.5 rounded-lg bg-[#f2e8e1] px-2 text-[7px] text-muted-foreground">
          <Search className="h-2.5 w-2.5" />
          Search mail
        </div>
      </div>
      <div className="border-t border-[#ede0d7] px-3 py-2">
        <div className="flex items-center gap-2 rounded-lg bg-[#fff1e7] p-2">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[8px] font-extrabold">Maya Rodriguez</p>
            <p className="mt-0.5 truncate text-[8px] font-semibold">Launch week: final checklist</p>
            <p className="mt-0.5 truncate text-[7px] text-muted-foreground">I pulled everything together…</p>
          </div>
          <span className="text-[6px] text-muted-foreground">9:42</span>
        </div>
      </div>
      {messages.slice(1, 4).map((message) => (
        <div key={message.name} className="flex gap-2 border-b border-[#f0e4db] px-3 py-2">
          <span
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[6px] font-bold ${message.color}`}
          >
            {message.initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex justify-between gap-1">
              <p className="truncate text-[7px] font-bold">{message.name}</p>
              <span className="text-[6px] text-muted-foreground">{message.time}</span>
            </div>
            <p className="mt-0.5 truncate text-[7px] font-semibold">{message.subject}</p>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-around bg-[#35221d] px-4 py-2 text-white/65">
        <Inbox className="h-3 w-3 text-[#ff9b65]" />
        <Search className="h-3 w-3" />
        <Send className="h-3 w-3" />
        <Settings className="h-3 w-3" />
      </div>
    </div>
  )
}

function AppShowcase() {
  return (
    <div className="relative mx-auto mt-14 max-w-[1140px] pb-10 md:mt-18 lg:pb-24">
      <div className="absolute inset-x-12 top-10 h-64 rounded-full bg-[#ffb88d]/25 blur-3xl" />
      <div className="relative z-10 lg:mr-12 xl:mr-20">
        <DesktopInboxPreview />
      </div>
      <div className="relative z-20 mx-auto mt-5 max-w-[370px] xl:absolute xl:-bottom-12 xl:-right-5 xl:mt-0">
        <AccountSetupPreview />
      </div>
      <div className="absolute -bottom-1 left-1 z-20 hidden xl:block">
        <MobileInboxPreview />
      </div>
      <div className="absolute -top-3 right-[12%] z-20 hidden items-center gap-2 rounded-full border border-primary/20 bg-card/95 px-3 py-1.5 text-[9px] font-bold text-primary shadow-lg lg:flex">
        <Smartphone className="h-3 w-3" />
        Same inbox. Every screen.
      </div>
    </div>
  )
}

export function Hero() {
  return (
    <section className="relative overflow-hidden pb-24 pt-14 md:pb-36 md:pt-20">
      <div className="absolute inset-0 grid-bg opacity-70 [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />
      <div className="absolute -left-28 top-20 h-72 w-72 rounded-full bg-[#ffcfaf]/50 blur-3xl" />
      <div className="absolute -right-24 top-40 h-80 w-80 rounded-full bg-[#ffd6c0]/45 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4">
        <div className="float-in mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/80 px-3 py-1.5 text-xs font-semibold text-primary shadow-sm">
            <span className="h-2 w-2 rounded-full bg-primary" />
            One app · Windows, Mac, Linux, Android &amp; iOS
          </span>
          <h1 className="mt-6 text-balance text-[2.75rem] font-extrabold leading-[1.02] tracking-[-0.055em] sm:text-6xl md:text-7xl">
            Your inbox,
            <br />
            <span className="text-primary">without the noise.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
            The same focused email app on every device. NewEmail brings iCloud, Gmail, Outlook,
            and standards-based mail into one fast workspace wherever you work.
          </p>
          <PlatformTicker />
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              className="min-w-44"
              nativeButton={false}
              render={<a href="/downloads" />}
            >
              Download NewEmail
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              nativeButton={false}
              render={<a href="/forum" />}
            >
              Explore the community
            </Button>
            <Button
              variant="ghost"
              size="lg"
              nativeButton={false}
              render={
                <a
                  href="https://github.com/ricktek954/newemail"
                  target="_blank"
                  rel="noreferrer"
                />
              }
            >
              <Code2 className="h-4 w-4" />
              View source
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Universal IMAP &amp; SMTP · Local-first speed · No mail data sold
          </p>
        </div>
        <AppShowcase />
      </div>
    </section>
  )
}
